import { validateReturnUrl } from '@hello-world-dao/auth';

/**
 * Return-to-origin (open-redirect-safe) for the registration front-door.
 *
 * uid-001-3b AC3. A person can arrive at /register from any DAO/FOS property
 * carrying a `returnUrl` query param; on a successful 201 we send them back to
 * exactly where they came from. The redirect target MUST be validated against
 * an explicit allowlist or this becomes a textbook open-redirect (CWE-601).
 *
 * The registration front-door is reached cross-origin from BOTH the
 * founderyos.dev and helloworlddao.com suite hosts. bl-997 added an
 * `allowedDomains` knob to the shared, audited `validateReturnUrl` from
 * `@hello-world-dao/auth`, so we now delegate the ENTIRE open-redirect gate
 * to that helper and pass both brand families' domains (bl-998). This drops the
 * former bolt-on `matchCrossOriginAllowlist` layer — the shared helper is the
 * single audited gate.
 *
 * Domain-allowlist semantics (from the shared helper):
 *   - `'.helloworlddao.com'` / `'.founderyos.dev'` match the apex AND any
 *     subdomain, HTTPS-only. Look-alikes (`evil-founderyos.dev`,
 *     `founderyos.dev.evil.com`) are rejected — the helper never does a naive
 *     suffix `endsWith`.
 *   - Relative paths are validated against the helper's built-in path
 *     allowlist; anything unmatched collapses to `DEFAULT_RETURN_PATH`.
 */

/** Safe default when no (or an invalid) returnUrl is supplied. */
export const DEFAULT_RETURN_PATH = '/register';

/**
 * Trusted return-URL domains for cross-suite/cross-brand SSO redirects (bl-998).
 *
 * Extends the shared helper's baked default (`['.helloworlddao.com']`) with the
 * founderyos.dev brand family so a founderyos.dev-zone caller's absolute
 * returnUrl survives the gate. Leading-dot form = apex + any subdomain, HTTPS
 * only (see {@link validateReturnUrl}). The default entry is passed explicitly
 * so we EXTEND rather than REPLACE the helper's helloworlddao.com trust.
 */
export const ALLOWED_RETURN_DOMAINS: readonly string[] = [
  '.helloworlddao.com',
  '.founderyos.dev',
];

/**
 * Fallback DAO-portal origin for a DIRECT login with no returnUrl.
 *
 * A member who navigates to `/login` directly (no `return_origin` / `returnUrl`
 * / `redirect_uri`) must land on their account home — NOT the `/register`
 * front-door (the bug Coby hit on staging: a no-returnUrl login dead-ended on
 * "Create your account"). This is the LOGIN flow's default only; REGISTER's
 * no-returnUrl behavior (DEFAULT_RETURN_PATH → /register → /login continuation)
 * is intentionally separate and unchanged.
 *
 * ⚑ This is the **DAO brand's** account home. See {@link STAGING_FOS_APP_RETURN_URL}
 * for the FounderyOS one and {@link loginDefaultReturnUrl} for how the serving
 * host picks between them (bl-1322).
 *
 * Build-time env override (`VITE_DEFAULT_LOGIN_RETURN_URL`); the compiled-in
 * fallback is the staging portal. It MUST be an allowlisted absolute origin
 * (it is — `.helloworlddao.com` is trusted) so it survives `resolveReturnUrl`'s
 * open-redirect gate as a cross-origin handoff: LoginPage sends it as
 * `return_origin`, the server mints `?code=`, the portal exchanges it → the
 * member lands logged in.
 */
export const STAGING_PORTAL_RETURN_URL =
  'https://portal.staging.helloworlddao.com';

/**
 * Fallback **FounderyOS** app origin for a DIRECT login with no returnUrl (bl-1322).
 *
 * The FOS-brand mirror of {@link STAGING_PORTAL_RETURN_URL}. `dashboard.staging.
 * founderyos.dev` is the FounderyOS dashboard's staging host (its
 * `deploy/k8s/ingress.yaml`), it is already in identity-service's
 * `DEFAULT_RETURN_ORIGINS` so the `?code=` handoff is authorized server-side, and
 * its `/` route is the authenticated Command Center behind `ProtectedRoute` —
 * i.e. authenticated FOS content, which is exactly what AC2 asks for.
 *
 * Build-time env override: `VITE_DEFAULT_LOGIN_RETURN_URL_FOS` (the prod FOS app
 * origin at cutover). Like the DAO knob it runs through `resolveReturnUrl`, so a
 * misconfigured value collapses to the safe relative default instead of emitting
 * an unvalidated origin.
 */
export const STAGING_FOS_APP_RETURN_URL =
  'https://dashboard.staging.founderyos.dev';

/**
 * Which brand this build is currently *serving* (bl-1322).
 *
 * `'dao'` = Hello World DAO (helloworlddao.com) · `'fos'` = FounderyOS
 * (founderyos.dev).
 */
export type Brand = 'dao' | 'fos';

/** Registrable apex of the FounderyOS brand family. */
const FOS_APEX = 'founderyos.dev';

/**
 * Resolve a hostname to the brand it belongs to.
 *
 * ⚠️ ONE DEPLOYMENT SERVES BOTH BRANDS. `deploy/k8s/ingress.yaml` routes
 * `register.staging.founderyos.dev` AND `register.staging.helloworlddao.com` to
 * the same Service/Deployment, so a single build-time constant cannot be correct
 * for both. The brand is therefore a property of the REQUEST, read from the host
 * the member is actually on.
 *
 * MATCHING: exact apex OR a dot-anchored subdomain — the same shape the shared
 * `validateReturnUrl` allowlist uses, and deliberately NOT a bare
 * `endsWith('founderyos.dev')`, which would hand the FOS brand to
 * `evil-founderyos.dev`.
 *
 * FAIL-CLOSED: anything that is not provably FOS resolves to `'dao'` — the
 * pre-bl-1322 behavior. A mis-parsed, empty or unknown host can therefore only
 * ever reproduce the old destination, never invent a new one.
 *
 * A single trailing dot (the fully-qualified `founderyos.dev.` form, which a
 * browser will happily put in `location.hostname` if it is navigated to) is
 * stripped before matching. Without that, `register.staging.founderyos.dev.`
 * classifies as DAO and the member gets the II gate — fail-closed, but still the
 * bug. Stripping it opens nothing: `evil-founderyos.dev.` normalizes to
 * `evil-founderyos.dev`, which is still not dot-anchored under the apex.
 *
 * @param hostname a bare hostname (no scheme, no port), e.g. `window.location.hostname`
 */
export function brandForHostname(hostname: string | null | undefined): Brand {
  const host = (hostname ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if (host === FOS_APEX || host.endsWith(`.${FOS_APEX}`)) {
    return 'fos';
  }
  return 'dao';
}

/**
 * The compiled-in account home for a brand — guaranteed to be an allowlisted
 * absolute origin, with NO env override applied.
 *
 * Callers that must produce an absolute URL use this as their last resort. The
 * env-overridable {@link loginDefaultReturnUrl} cannot serve that role: a
 * misconfigured override is (correctly) collapsed by the open-redirect gate to
 * the RELATIVE {@link DEFAULT_RETURN_PATH}, and feeding that to `new URL()`
 * throws `TypeError: Invalid URL`.
 */
function compiledBrandHome(brand: Brand): string {
  return brand === 'fos'
    ? STAGING_FOS_APP_RETURN_URL
    : STAGING_PORTAL_RETURN_URL;
}

/**
 * The brand of the host this page is being served from, read AT CALL TIME.
 *
 * Not memoized and not captured at module load: the point of bl-1322 is that the
 * answer is a runtime property of the serving host, not a build-time constant.
 * Outside a browser (unit tests without a DOM, any future SSR) there is no host,
 * so this falls back to `'dao'` via {@link brandForHostname}'s fail-closed rule.
 */
export function currentBrand(): Brand {
  if (typeof window === 'undefined' || !window.location) {
    return 'dao';
  }
  return brandForHostname(window.location.hostname);
}

/**
 * The configured default account home for a direct (no-returnUrl) login,
 * BRANCHED ON THE SERVING BRAND (bl-1322) and validated through the SAME
 * open-redirect gate every caller uses (`resolveReturnUrl`).
 *
 * ⚖️ THE RULING (Coby, 2026-08-10): *"When registering and landing on the FOS site
 * we should only be landing on the verified email without asking to add II. II is
 * only a step when registering for the DAO."* Before this branch existed, EVERY
 * bare `/login` — on either brand host — defaulted to the DAO portal, whose
 * `AppLayout` computes `needsOnboarding = isAuthenticated && !icPrincipal` and
 * pushes an account with no `ic_principal` (i.e. every FOS account) into the
 * forced Internet-Identity link gate.
 *
 * ⛔ The DAO branch is unchanged and MUST stay unchanged: `epic-onb-001`'s forced
 * II-link is correct on the DAO path. Both directions are pinned by tests in
 * `returnUrl.test.ts` so a later edit cannot quietly swap which platform gets it.
 *
 * If the env value is somehow non-allowlisted (misconfiguration), the gate
 * collapses it to the safe relative {@link DEFAULT_RETURN_PATH} rather than
 * emitting an unvalidated origin — true on BOTH branches. Callers that need a
 * guaranteed-absolute origin must therefore handle the relative result; see
 * {@link postVerifyDestination}.
 *
 * The serving brand is read here rather than taken as a parameter: an injectable
 * brand would be a way to hard-code the answer at a call site, which is the exact
 * defect this story fixes.
 */
export function loginDefaultReturnUrl(): string {
  const brand = currentBrand();
  const configured =
    brand === 'fos'
      ? (import.meta.env.VITE_DEFAULT_LOGIN_RETURN_URL_FOS ??
        STAGING_FOS_APP_RETURN_URL)
      : (import.meta.env.VITE_DEFAULT_LOGIN_RETURN_URL ??
        STAGING_PORTAL_RETURN_URL);
  return resolveReturnUrl(configured);
}

/**
 * Validate a raw returnUrl query param into a safe redirect target.
 *
 * Delegates entirely to the shared, audited `validateReturnUrl`, passing the
 * cross-brand {@link ALLOWED_RETURN_DOMAINS} allowlist (bl-998). Absolute HTTPS
 * URLs to a trusted domain are returned verbatim (path/query preserved);
 * relative paths are validated against the helper's built-in path allowlist;
 * everything else (evil origins, look-alikes, dangerous schemes, http variants
 * of trusted hosts) collapses to {@link DEFAULT_RETURN_PATH}.
 *
 * @param raw the untrusted `returnUrl` (or null/undefined if absent)
 * @returns a safe absolute URL (allowlisted cross-origin) or safe path/default
 */
export function resolveReturnUrl(raw: string | null | undefined): string {
  if (!raw) {
    return DEFAULT_RETURN_PATH;
  }

  return validateReturnUrl(raw, {
    defaultRedirect: DEFAULT_RETURN_PATH,
    allowedDomains: ALLOWED_RETURN_DOMAINS,
  });
}

/**
 * True when the resolved target is an allowlisted cross-origin absolute URL
 * (so the caller does a full-page `window.location.assign`) vs a same-app
 * relative route (so the caller can use client routing).
 */
export function isAbsoluteReturnUrl(resolved: string): boolean {
  return resolved.startsWith('http://') || resolved.startsWith('https://');
}

/**
 * The DAO portal's onboarding path — `epic-onb-001`'s forced Internet-Identity
 * link gate lives behind it (bl-795). ⛔ DAO-ONLY. It creates an on-chain DAO
 * membership; it is not a step in a FounderyOS signup (bl-1322).
 */
export const ONBOARDING_PATH = '/onboarding';

/**
 * Build the post-verify-email onward destination (bl-813 AC4, brand-branched by
 * bl-1322).
 *
 * On a successful email-verify we send the member to their brand's account home:
 *
 *   - **DAO** → `<portal-origin>/onboarding`, the II-link step. Unchanged.
 *   - **FOS** → the FOS app origin, with **NO** `/onboarding` segment. Per the
 *     ruling, a verified email is the whole requirement for FounderyOS; the DAO's
 *     II gate has no place in that flow.
 *
 * ⚑ THE BRAND COMES FROM THE RESOLVED TARGET, NOT ONLY THE SERVING HOST. When no
 * `returnUrl` is carried — the usual case, since the email link identity-service
 * builds carries only `?token=` — the target IS the serving brand's default, so
 * the two agree. When a `returnUrl` IS threaded, the brand of that origin is what
 * matters: appending the DAO's `/onboarding` to a founderyos.dev origin was the
 * pre-bl-1322 behavior and is precisely the defect.
 *
 * This still hardcodes no host (the origins come from the brand defaults, both
 * env-overridable), so the same build stays correct on both zones. An evil /
 * relative / rejected target collapses to the serving brand's default via the
 * shared open-redirect gate — never reaching the wire.
 *
 * ⚠️ The last-resort fallback is the COMPILED-IN brand home, not
 * `loginDefaultReturnUrl()`. That matters: when the env override is
 * misconfigured, `loginDefaultReturnUrl()` correctly collapses to the RELATIVE
 * `DEFAULT_RETURN_PATH`, and `new URL('/register')` throws `TypeError: Invalid
 * URL` — inside this page's `useMemo`, i.e. a blank verify-email page on a
 * mis-deployed build. The compiled-in constant is always an allowlisted absolute
 * origin, so it degrades instead of crashing without widening the redirect gate.
 *
 * @param raw the untrusted `returnUrl` (or null/undefined if absent — the usual case)
 * @returns an allowlisted absolute URL: `<origin>/onboarding` on DAO, `<origin>` on FOS
 */
export function postVerifyDestination(raw: string | null | undefined): string {
  // Resolve an origin: an allowlisted cross-origin returnUrl, else the serving
  // brand's default (which itself passes the same open-redirect gate).
  const resolved = raw == null ? loginDefaultReturnUrl() : resolveReturnUrl(raw);
  const base = isAbsoluteReturnUrl(resolved)
    ? resolved
    : compiledBrandHome(currentBrand());
  const { origin, hostname } = new URL(base);
  // The DAO's onboarding gate is appended ONLY on a DAO origin. Any path the
  // caller carried is dropped either way — the continuation target is fixed.
  return brandForHostname(hostname) === 'fos'
    ? origin
    : `${origin}${ONBOARDING_PATH}`;
}

/**
 * Build the post-register destination.
 *
 * Register issues NO session (uid-001) — the next step is sign-in at `/login`,
 * which mints the JWT + the cross-domain handoff code. So a successful register
 * must continue to `/login`, NEVER dead-end on the front-door placeholder.
 *
 * The `raw` returnUrl is run through the SAME open-redirect gate
 * (`resolveReturnUrl`) the register/login pages use — no validation is skipped
 * or weakened here. Only a target that survives the gate as an allowlisted
 * cross-origin absolute URL is forwarded to `/login` (as a `returnUrl` query
 * param) so login can complete the cross-domain handoff back to the caller.
 * A relative / default / rejected target produces a bare `/login` (a direct,
 * central-origin sign-in with no cross-domain code).
 *
 * @param raw the untrusted `returnUrl` (or null/undefined if absent)
 * @returns a same-app relative path: `/login` or `/login?returnUrl=<encoded>`
 */
export function postRegisterDestination(raw: string | null | undefined): string {
  const resolved = resolveReturnUrl(raw);
  if (isAbsoluteReturnUrl(resolved)) {
    // Allowlisted cross-origin caller — forward it so /login completes the
    // handoff. The value already passed the open-redirect gate above.
    return `/login?returnUrl=${encodeURIComponent(resolved)}`;
  }
  // Relative / default / rejected → plain /login (no cross-domain code).
  return '/login';
}
